import {
  ButtonElement,
  CommonPageHeader,
  PageSwitcher,
  Search,
  TableHeaders,
} from '@/react/components'
import { css } from '@emotion/react'
import React from 'react'
import { ThinCheckmark } from '@/react/componentAssets/ThinCheckmark'
import { NavBar } from '../LMSCommonComponents/NavBar'
import { EmployeeRow } from './components/EmployeeRow'
import sortArrows from '@/assets/icons/sort-arrows.svg'
import sortArrowsActiveFirst from '@/assets/icons/sort-arrows-active.svg'
import sortArrowsActiveSecond from '@/assets/icons/sort-arrows-inactive.svg'
import { hexToRGBAWithOpacity } from '@/utils/generalUtilities.js'
import { InfoModal } from '@/react/componentAssets/InfoModal'
import { InfoIcon } from '@/react/componentAssets/InfoIcon'
import { useTrainingEmployeeListState } from './hooks/useTrainingEmployeeListState'
import { singleHeaderTypes } from '../../components/types'

export const TrainingEmployeeList = () => {
  const {
    order,
    training,
    employeeList,
    variableColor,
    page,
    isAllChecked,
    search,
    count,
    isPopUpVisible,
    listToAssign,
    setOrder,
    setPage,
    setSearch,
    setPopUpVisible,
    handleCheckedAll,
    handleTogglePopup,
    handleAssign,
    handleButtonClick,
  } = useTrainingEmployeeListState()

  const TABLE_HEADER_SETTINGS: singleHeaderTypes[] = [
    {
      className: 'template',
      name: 'Employees',
      multiType: ['name', 'first_name', 'last_name'],
      filterHeader: true,
      additionalStyles: css({
        textAlign: 'left',
        paddingLeft: '18px',
        width: '90%',
        borderBottom: '2px solid #C6C6C6',
        p: {
          display: 'flex',
          gap: '4px',
          width: 'fit-content',
          margin: '0 !important',
        },
        '.header-base': {
          display: 'flex',
          alignItems: 'center',
          width: '100px',
          position: 'relative',
        },
      }),
      additionalInnerElement: (
        <div className="info__wrapper">
          <InfoIcon handleClick={handleTogglePopup} color={variableColor} />
          {isPopUpVisible ? (
            <InfoModal
              message={
                'This list is only displaying active employees. If you do not see an employee here, you should activate them from the User List.'
              }
              setModalOpen={setPopUpVisible}
            />
          ) : null}
        </div>
      ),
    },
    {
      className: 'table__header',
      name: '',
      type: 'checkbox',
      additionalStyles: css({
        width: '46px',
        minWidth: '46px',
        paddingLeft: 0,
        textAlign: 'left',
        borderBottom: '2px solid #C6C6C6',
        paddingInline: 0,
        p: {
          width: '0px',
        },
        '.header-base': {
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingRight: '13px',
        },
      }),
      additionalInnerElement: (
        <div className="select__wrapper">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={handleCheckedAll}
            />
            <span className="checkmark">
              <ThinCheckmark color={variableColor} />
            </span>
          </label>
          <img
            src={
              order === 'assigned'
                ? sortArrowsActiveSecond
                : order === '-assigned'
                ? sortArrowsActiveFirst
                : sortArrows
            }
            className="sort__icon"
            alt="sort icon"
            onClick={() => {
              setOrder(order === '-assigned' ? 'assigned' : '-assigned')
            }}
          />
        </div>
      ),
    },
  ]

  const baseStyle = getBaseStyle(variableColor)

  return (
    <div css={baseStyle}>
      <CommonPageHeader headerText={'Learning Management System'} />
      <NavBar />
      <section className="assign-employee">
        <div className="top__wrapper">
          <h4>{training.name || ''}</h4>
          <ButtonElement
            buttonType="submit"
            text="Assign"
            functionToTrigger={() => void handleButtonClick()}
            additionalStyles={css({ padding: 0 })}
          />
        </div>
        <Search
          search={search}
          searchCallback={setSearch}
          pageCallback={setPage}
          updateUrl={false}
        />
        <table>
          <TableHeaders
            headers={TABLE_HEADER_SETTINGS}
            callback={setOrder}
            popUpLocation={false}
            updateUrl={false}
            order={order}
            setOrder={setOrder}
          />
          {employeeList?.map((employee, index) => (
            <EmployeeRow
              key={employee.id}
              employee={employee}
              index={index}
              search={search}
              handleAssign={handleAssign}
              listToAssign={listToAssign}
            />
          ))}
        </table>
        <div className="page-switcher-base">
          <PageSwitcher
            pageLength={20}
            listCount={count}
            currentPage={page}
            callback={setPage}
            updateUrl={false}
          />
        </div>
      </section>
    </div>
  )
}

function getBaseStyle(variableColor: string) {
  return css({
    paddingBottom: '200px',
    '.assign-employee': {
      background: '#fff',
      paddingLeft: '20px',
      width: '834px',
    },
    '.select__wrapper': {
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '.top__wrapper': {
      display: 'flex',
      gap: '30px',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '20px',
      paddingRight: '20px',
      paddingBottom: '20px',
    },
    '.checkbox-container': {
      display: 'inline-block',
      position: 'relative',
      cursor: 'pointer',
      paddingLeft: '15px',
      margin: 0,
      height: '20px',
    },
    '.checkmark': {
      height: '22px',
      width: '22px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid #E6E6E6',
      background: '#fff',
      svg: {
        display: 'none',
      },
    },
    '.checkmark::after': {
      display: 'none !important',
    },
    'input:checked ~ .checkmark': {
      img: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
      svg: {
        height: '22px',
        width: '22px',
        display: 'block',
        background: 'white',
      },
    },
    '.project__name': {
      fontSize: '13px',
      margin: 0,
    },
    table: {
      width: '791px',
    },
    '.project__row': {
      position: 'relative',
      height: '40px',
    },
    '.name__td': {
      paddingLeft: '16px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '650px',
    },
    '.dark': {
      background: '#F2F3F5',
      borderBottom: '1px solid #E7EAEC',
      borderTop: '1px solid #E7EAEC',
    },
    '.group__row': {
      position: 'relative',
      height: '40px',
      background: hexToRGBAWithOpacity(variableColor, 0.25),
    },
    '.light': {
      background: hexToRGBAWithOpacity(variableColor, 0.1),
    },
    '.checkmark__td': {
      textAlign: 'center',
      minWidth: '80px',
    },
    '.page-switcher-base': {
      paddingBottom: '20px',
      marginBottom: '20px',
    },
    '.name__cell': {
      paddingLeft: '16px',
    },
    '.full__name': {
      position: 'absolute',
      zIndex: 1,
      border: '1px solid #D3D3D3',
      boxShadow: '2px 4px 7px 0px rgba(0, 0, 0, 0.15)',
      background: 'white',
      top: '40px',
      left: '153px',
      padding: '4px 11px',
      maxWidth: '550px',
    },
    '.edit__cell': {
      display: 'flex',
      gap: '4px',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    '.template__row': {
      height: '40px',
    },
    '.highlighted__text': {
      backgroundColor: hexToRGBAWithOpacity(variableColor),
    },
    '.info__wrapper': {
      position: 'absolute',
      right: '15px',
      top: '-3px',
    },
    '.info__icon': {
      cursor: 'pointer',
    },
    '.sort__icon': {
      cursor: 'pointer',
      height: '17px',
    },
    '@media(max-width: 1050px)': {
      '#search': {
        width: '264px',
      },
      '.top__wrapper': {
        paddingRight: 0,
        gap: '8px',
      },
      '.assign-employee': {
        width: '100%',
        paddingRight: '23px',
      },
      table: {
        width: '100%',
      },
      '.name__td': {
        maxWidth: '204px',
      },
    },
  })
}
